// API client for communicating with Lambda functions

const API_BASE = import.meta.env.VITE_API_BASE || '';

// ============================================
// Types
// ============================================

export interface ChatRequest {
  message: string;
  // Server-minted UUID; omitted on the first message of a session
  sessionId?: string;
}

export interface ChatResponse {
  message: string;
  sessionId: string;
}

export interface AnalyzeJdRequest {
  jobDescription: string;
}

export type FitVerdict = 'strong_fit' | 'worth_conversation' | 'probably_not';

export interface FitGap {
  requirement: string;
  gap_title: string;
  explanation: string;
}

export interface AnalyzeJdResponse {
  verdict: FitVerdict;
  headline: string;
  opening: string;
  gaps: FitGap[];
  transfers: string;
  recommendation: string;
}

export interface PublicProfile {
  name: string;
  title: string;
  elevator_pitch?: string;
  availability_status?: string;
  location?: string;
  github_url?: string;
  linkedin_url?: string;
}

export interface PublicExperience {
  id: string;
  company_name: string;
  title: string;
  title_progression?: string;
  start_date?: string;
  end_date?: string;
  is_current: boolean;
  bullet_points: string[];
  display_order: number;
}

export interface PublicSkill {
  id: string;
  skill_name: string;
  category: 'strong' | 'moderate' | 'gap';
  years_experience?: number;
}

// ============================================
// API Functions
// ============================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }
  return response.json();
}

// Chat API
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  const response = await fetch(`${API_BASE}/api/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  return handleResponse<ChatResponse>(response);
}

// JD Analysis API
export async function analyzeJobDescription(request: AnalyzeJdRequest): Promise<AnalyzeJdResponse> {
  const response = await fetch(`${API_BASE}/api/analyze-jd`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  return handleResponse<AnalyzeJdResponse>(response);
}

// Public Profile API
export async function getPublicProfile(): Promise<PublicProfile> {
  const response = await fetch(`${API_BASE}/api/admin/public/profile`);
  return handleResponse<PublicProfile>(response);
}

// Public Experiences API
export async function getPublicExperiences(): Promise<PublicExperience[]> {
  const response = await fetch(`${API_BASE}/api/admin/public/experiences`);
  return handleResponse<PublicExperience[]>(response);
}

// Public Skills API
export async function getPublicSkills(): Promise<PublicSkill[]> {
  const response = await fetch(`${API_BASE}/api/admin/public/skills`);
  return handleResponse<PublicSkill[]>(response);
}

// ============================================
// Session Management
// ============================================

const SESSION_KEY = 'xavi_chat_session';

// Session IDs are minted server-side; we only store the one the server returns
export function getStoredSessionId(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

export function storeSessionId(id: string): void {
  sessionStorage.setItem(SESSION_KEY, id);
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}
